import React, { useEffect, useRef } from 'react';

import { AttachedContractsLayout } from '@cube3/ui/src/Layout/AttachedContractsLayout';
import { ResourceIdentifier } from '@cube3/common/model/resource-types';
import { ContractsList } from '../prefabs/ContractList';
import {
  RelatedContractsNotice,
  ProjectUrlParams
} from '../prefabs/RelatedContractsNotice';
import { useResourceList__ALPHA } from '@cube3/state/src/redux/components/Hooks/useResourceList';
import { Padding } from '../prefabs/Padding';
import { ContractView } from './ContractView';
import { AttachedContractsCommandBar } from '../prefabs/command-bars/AttachedContractsCommandBar';
import { useRouteMatch } from 'react-router';
import { urlStructureProject } from '../../../routing/routingPaths';
import { Typography } from '@cube3/cubicle/src/core/atoms/Typography/Typography';
import { VBox } from '@cube3/cubicle/src/core/templates/layout/Flex';
import { useUiSettings } from '../../ContentTree/hooks/useUiSettings';
import { isMobile } from 'react-device-detect';

interface Props {
  parentResource?: ResourceIdentifier;
}

const AttachedContractsViewBase: React.ComponentType<Props> = ({
  parentResource
}) => {
  const attachedContracts = useResourceList__ALPHA({
    resourceType: parentResource.type,
    resourceId: parentResource.id,
    edgeType: 'contract',
    edgeLabel: 'contracts',
    params: useRef({
      page: {
        size: -1
      },
      sort: 'expires_at'
    }).current,
    strategy: 'fetch-on-mount'
  });

  const { projectId } =
    useRouteMatch<ProjectUrlParams>(urlStructureProject)?.params || {};

  const projectContracts = useResourceList__ALPHA({
    resourceType: 'project',
    resourceId: parentResource.type === 'asset' && projectId,
    edgeType: 'contract',
    edgeLabel: 'contracts',
    params: useRef({
      page: {
        size: 1
      }
    }).current
  });

  const { selectedContract: selection, selectContract: setSelection } =
    useUiSettings();

  const contractCount = attachedContracts.pageCounts?.itemCount;

  useEffect(() => {
    if ((!selection || !attachedContracts.resources?.find(c => c.id === selection.id)) && attachedContracts.first) {

      setSelection(attachedContracts.first);
    }
    // if (contractCount === 0 || attachedContracts.loading) {
    //   setSelection(null);
    // }
  }, [selection, attachedContracts.first, contractCount]);

  return (
    <AttachedContractsLayout
      loading={attachedContracts.loading || projectContracts.loading}
      menuElement={
        <AttachedContractsCommandBar
          contract={selection as ResourceIdentifier}
          parentResource={parentResource}
          mobileSliderLevel="first"
        />
      }
      noticeElement={
        parentResource.type === 'asset' && <RelatedContractsNotice />
      }
      listElement={<ContractsList all={true} parentResource={parentResource} />}
      detailElement={
        isMobile ? undefined : selection &&
          attachedContracts.resources?.length ? (
          <ContractView contractId={selection.id} />
        ) : (
          <Padding>
            <VBox>
              <Typography weight="label" colorVariant="02">
                No contracts attached to this {parentResource.type}
              </Typography>
              <Typography colorVariant="03">
                Use the menu to attach existing contracts or create a new one
              </Typography>
            </VBox>
          </Padding>
        )
      }
      listHeaderElement={
        <Padding padding={isMobile ? 8 : 16}>
          <Typography>
            {(attachedContracts.pageCounts?.itemCount || 0) +
              (projectContracts.pageCounts?.itemCount || 0)}{' '}
            contracts
          </Typography>
        </Padding>
      }
    />
  );
};

export const AttachedContractsView = ({ parentResource }) => {
  return <AttachedContractsViewBase parentResource={parentResource} />;
};
